'use client';

import { Dialog } from '@base-ui/react/dialog';
import { useFeatureInteracted } from '@repo/hooks/useFeatureInteracted';
import { css, cx } from '@repo/pandacss/styled-system/css';
import { Stack } from '@repo/pandacss/styled-system/jsx';
import { FeatureViewed } from '@repo/segment/components/FeatureViewed';
import { useCallback } from 'react';
import { triggerStyles } from './constants';
import { getDialogContent } from './content';
import { type MoreInfoDialogProps } from './types';

function MoreInfoDialogContent({ id, title, children, triggerClassName }: MoreInfoDialogProps) {
  const fireFeatureInteracted = useFeatureInteracted({
    entityName: `${id}_learn_more_pop_up_close`,
    entitySection: `${id}_learn_more_pop_up`,
  });

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        fireFeatureInteracted();
      }
    },
    [fireFeatureInteracted],
  );

  return (
    <Dialog.Root onOpenChange={handleOpenChange}>
      <Dialog.Trigger className={cx(triggerStyles, triggerClassName)} type="button">
        {children}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Backdrop
          className={css({
            position: 'fixed',
            top: '0',
            right: '0',
            bottom: '0',
            left: '0',
            backgroundColor: '[rgba(0, 0, 0, 0.5)]',
            zIndex: '[1100]',
          })}
        />
        <Dialog.Popup
          className={css({
            position: 'fixed',
            top: '[50%]',
            left: '[50%]',
            transform: '[translate(-50%, -50%)]',
            maxWidth: '[600px]',
            maxHeight: '[90vh]',
            overflowX: 'auto',
            overflowY: 'auto',
            backgroundColor: 'white',
            borderTopLeftRadius: '2',
            borderTopRightRadius: '2',
            borderBottomRightRadius: '2',
            borderBottomLeftRadius: '2',
            boxShadow:
              '[0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)]',
            paddingBlockStart: '6',
            paddingBlockEnd: '6',
            paddingInlineStart: '8',
            paddingInlineEnd: '8',
            zIndex: '[1100]',
          })}
        >
          <FeatureViewed
            entityName={`${id}_learn_more_pop_up`}
            entitySection={`${id}_learn_more_pop_up`}
          >
            <div>
              <Stack
                direction="row"
                columnGap="6"
                rowGap="6"
                className={css({ marginBottom: '6' })}
              >
                <Dialog.Title
                  className={css({
                    textStyle: 'textLargeBold',
                    fontWeight: 'medium',
                  })}
                >
                  {title}
                </Dialog.Title>
                <Dialog.Close
                  aria-label="Close dialog"
                  className={css({
                    width: '5',
                    height: '5',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: 'xl',
                    borderTopWidth: 'none',
                    borderRightWidth: 'none',
                    borderBottomWidth: 'none',
                    borderLeftWidth: 'none',
                    borderTopStyle: 'none',
                    borderRightStyle: 'none',
                    borderBottomStyle: 'none',
                    borderLeftStyle: 'none',
                  })}
                >
                  ✕
                </Dialog.Close>
              </Stack>
              <Dialog.Description render={() => <div>{getDialogContent(id)}</div>} />
            </div>
          </FeatureViewed>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default MoreInfoDialogContent;
